import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';

import { Modal } from '@/components/admin/Modal';
import { CountBadge } from '@/components/admin/CountBadge';
import { Notice } from '@/components/admin/Notice';
import {
  reissueInvitation,
  revokeInvitation,
  type Invitation,
  type InvitationCreated,
} from '@/features/invitations/api';

/**
 * 「招待中（まだ受諾されていません）」の一覧（D-140）と、その行操作。
 *
 * 受諾URLはトークンのハッシュしか保存していないため、**一度閉じると二度と表示できない**。
 * 「URLを教えて」と言われたときは「リンクを作り直す」で新しいリンクを出す（D-153）。
 * 作り直すと古いリンクは使えなくなるので、漏れたリンクの無効化も兼ねる。
 */
export function PendingInvitations({
  invitations,
  invalidateKeys,
  describe,
}: {
  invitations: Invitation[];
  /** 取り消し・再発行のあとに作り直すクエリキー */
  invalidateKeys: unknown[][];
  /** メールアドレスの後ろに出す補足（権限区分など） */
  describe?: (invitation: Invitation) => string;
}): ReactElement | null {
  const queryClient = useQueryClient();
  const [reissued, setReissued] = useState<InvitationCreated | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  async function refresh(): Promise<void> {
    for (const key of invalidateKeys) {
      await queryClient.invalidateQueries({ queryKey: key });
    }
  }

  const revokeMutation = useMutation({
    mutationFn: (invitationId: string) => revokeInvitation(invitationId),
    onSuccess: () => void refresh(),
    onError: () => setError('招待の取り消しに失敗しました。'),
  });

  const reissueMutation = useMutation({
    mutationFn: (invitationId: string) => reissueInvitation(invitationId),
    onSuccess: async (created) => {
      setCopied(false);
      setReissued(created);
      await refresh();
    },
    onError: () => setError('リンクの作り直しに失敗しました。'),
  });

  if (invitations.length === 0 && !reissued) return null;

  return (
    <>
      {error && (
        <Notice kind="error" onDismiss={() => setError(null)}>
          {error}
        </Notice>
      )}
      {invitations.length > 0 && (
        <div className="mb-4 rounded-sm border border-wpborder bg-white px-3 py-3">
          <p className="mb-2 inline-flex items-center gap-1.5 font-bold">
            招待中（まだ受諾されていません）
            <CountBadge count={invitations.length} label="未受諾" />
          </p>
          <ul className="space-y-1">
            {invitations.map((invitation) => (
              <li key={invitation.id} className="flex flex-wrap items-center justify-between gap-2">
                <span className="inline-flex items-center gap-1.5">
                  {invitation.email}（
                  {describe ? `${describe(invitation)}・` : ''}期限{' '}
                  {new Date(invitation.expires_at).toLocaleDateString('ja-JP')}）
                  <CountBadge count={1} label="未受諾" />
                </span>
                <span className="flex items-center gap-3">
                  <button
                    type="button"
                    disabled={reissueMutation.isPending}
                    onClick={() => reissueMutation.mutate(invitation.id)}
                    className="text-wpaccent underline disabled:opacity-50"
                  >
                    リンクを作り直す
                  </button>
                  <button
                    type="button"
                    disabled={revokeMutation.isPending}
                    onClick={() => revokeMutation.mutate(invitation.id)}
                    className="text-wpnotice-error underline disabled:opacity-50"
                  >
                    取り消す
                  </button>
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-wptext-muted">
            受諾用のリンクは、送ったあとに見返すことができません（安全のため保存していません）。
            「URLを教えて」と言われたときは「リンクを作り直す」を押してください。
            <strong>作り直すと、前に伝えたリンクは使えなくなります。</strong>
          </p>
        </div>
      )}

      <Modal
        open={reissued !== null}
        title="新しい受諾リンクを発行しました"
        onClose={() => setReissued(null)}
        wide
        footer={
          <button
            type="button"
            onClick={() => setReissued(null)}
            className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover"
          >
            閉じる
          </button>
        }
      >
        {reissued && (
          <>
            {reissued.mail_sent ? (
              <Notice kind="success" dismissible={false}>
                {reissued.invitation.email} 宛に、新しいリンクでメールを送り直しました。
              </Notice>
            ) : (
              <Notice kind="warning" dismissible={false}>
                <strong>メールは送っていません。</strong>
                いまはメールの送信が無効なため、リンクの作り直しだけを行いました。
                下のリンクを {reissued.invitation.email} のご本人に直接お伝えください。
              </Notice>
            )}
            <p className="mb-1 mt-3 text-wptext-muted">
              受諾用リンク（<strong>この画面を閉じると二度と表示できません</strong>。
              前のリンクはもう使えません）
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <code className="min-w-0 flex-1 break-all rounded-sm border border-wpborder bg-wpcanvas/60 p-2">
                {reissued.accept_url}
              </code>
              <button
                type="button"
                onClick={() => {
                  void navigator.clipboard?.writeText(reissued.accept_url);
                  setCopied(true);
                }}
                className="btn-secondary"
              >
                {copied ? 'コピーしました' : 'コピー'}
              </button>
            </div>
          </>
        )}
      </Modal>
    </>
  );
}
